/**
 * Practical, TYPEABLE time picker. You can either type the time naturally
 * ("2:30 pm", "230p", "14:30", "9a") or pick from the suggestion list.
 * Stores values as 24h "HH:MM" strings (what the backend expects) and
 * displays them as "h:mm AM/PM". Replaces the clunky native <input type="time">.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Clock } from 'lucide-react';

export function to12h(hhmm) {
  if (!hhmm || !hhmm.includes(':')) return hhmm || '';
  const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10));
  if (Number.isNaN(h)) return hhmm;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m || 0).padStart(2, '0')} ${ampm}`;
}

// Parse almost anything a human types into "HH:MM" 24h, or null.
export function parseTimeInput(raw) {
  if (!raw) return null;
  let s = String(raw).trim().toLowerCase();
  if (!s) return null;
  const isPm = /p/.test(s);
  const isAm = /a/.test(s);
  s = s.replace(/[^0-9:]/g, '');
  if (!s) return null;
  let h; let m = 0;
  if (s.includes(':')) {
    const [hp, mp] = s.split(':');
    h = parseInt(hp, 10);
    m = mp ? parseInt(mp.slice(0, 2), 10) : 0;
  } else if (s.length <= 2) {
    h = parseInt(s, 10);
  } else if (s.length === 3) {
    h = parseInt(s.slice(0, 1), 10);
    m = parseInt(s.slice(1), 10);
  } else {
    h = parseInt(s.slice(0, 2), 10);
    m = parseInt(s.slice(2, 4), 10);
  }
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  if (isPm && h < 12) h += 12;
  if (isAm && h === 12) h = 0;
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function buildTimeOptions(startHour = 6, endHour = 21, stepMin = 30) {
  const out = [];
  for (let h = startHour; h <= endHour; h += 1) {
    for (let mm = 0; mm < 60; mm += stepMin) {
      const v = `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
      out.push({ value: v, label: to12h(v) });
    }
  }
  return out;
}

const DEFAULT_OPTIONS = buildTimeOptions();

export function TimeSelect({
  value, onChange, options, placeholder = 'e.g. 2:30 PM',
  disabled = false, testId, className = '',
}) {
  const opts = options || DEFAULT_OPTIONS;
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [typed, setTyped] = useState(false);
  const wrapRef = useRef(null);

  // Keep the display synced to the external value when not actively typing.
  useEffect(() => {
    if (!focused) setText(value ? to12h(value) : '');
  }, [value, focused]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const commit = (t) => {
    const parsed = parseTimeInput(t);
    if (parsed) {
      onChange(parsed);
      setText(to12h(parsed));
    } else {
      setText(value ? to12h(value) : '');
    }
  };

  const filtered = (() => {
    const q = text.trim().toLowerCase();
    // Only filter once the user actually types — on plain focus/click show
    // the full list so a pre-filled value can be swapped with one click.
    if (!q || !typed) return opts;
    const parsed = parseTimeInput(q);
    if (parsed) {
      const hh = parsed.slice(0, 2);
      const sameHour = opts.filter((o) => o.value.slice(0, 2) === hh);
      if (sameHour.length) return sameHour;
    }
    const digits = q.replace(/[^0-9]/g, '');
    return opts.filter((o) => o.label.toLowerCase().includes(q)
      || o.value.includes(q)
      || (digits && o.value.replace(':', '').includes(digits)));
  })();

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <Clock size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          type="text"
          inputMode="numeric"
          disabled={disabled}
          value={text}
          placeholder={placeholder}
          data-testid={testId}
          className={`flex h-10 w-full rounded-md border border-input bg-background pl-7 pr-2 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
          onFocus={() => { setFocused(true); setOpen(true); setTyped(false); }}
          onChange={(e) => {
            setText(e.target.value);
            setTyped(true);
            setOpen(true);
            const parsed = parseTimeInput(e.target.value);
            if (parsed) onChange(parsed); // live update while typing
          }}
          onBlur={() => { setFocused(false); setOpen(false); commit(text); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(text); setOpen(false); e.target.blur(); }
            if (e.key === 'Escape') { setOpen(false); e.target.blur(); }
          }}
        />
      </div>
      {open && !disabled && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-md border border-slate-200 bg-white shadow-lg py-1"
          data-testid={testId ? `${testId}-list` : undefined}>
          {filtered.map((o) => (
            <button
              key={o.value}
              type="button"
              className={`block w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100 ${o.value === value ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-slate-700'}`}
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(o.value);
                setText(o.label);
                setOpen(false);
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default TimeSelect;
